import _ from 'lodash';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import Rewards from 'shared/rewards';
import types from 'frontend/state/actionTypes';

function userFieldReducer(field, initialState = null) {
  return (state = initialState, action = {}) => {
    const { type, payload } = action;
    switch (type) {
      case types.ON_LOGIN_SUCCESS:
      case types.ON_PREFERENCES_UPDATE:
      case types.ON_SUBSCRIPTION_UPDATE:
        return _.get(payload, field, null);
      case types.FETCH_STATUS_SUCCESS:
        return _.get(payload, `user.${field}`, null);
      default:
        return state;
    }
  };
}

export default combineReducers({
  created: userFieldReducer('created'),
  discord: userFieldReducer('discord'),
  email: userFieldReducer('email'),
  emailVerified: userFieldReducer('emailVerified'),
  emailVerifyLastRequest: userFieldReducer('emailVerifyLastRequest'),
  guildReward: userFieldReducer('guildReward'),
  key: userFieldReducer('key'),
  noAds: userFieldReducer('noAds'),
  queuePriority: userFieldReducer('queuePriority'),
  username: userFieldReducer('username'),
  patreonTitle: userFieldReducer('patreonTitle'),
  stripeCustomer: userFieldReducer('stripeCustomer'),
  stripeLinked: userFieldReducer('stripeLinked'),
  stripeChargeFailed: userFieldReducer('stripeChargeFailed'),
  stripeSubDeleted: userFieldReducer('stripeSubDeleted'),
  simTimeLimit: userFieldReducer('simTimeLimit'),
  admin: userFieldReducer('admin'),
  tooltipProvider: userFieldReducer('tooltipProvider', 'wowhead'),
  flagSimcBfa: userFieldReducer('flagSimcBfa', false),
  flagSmartSim: userFieldReducer('flagSmartSim', false),
  flagStripe: userFieldReducer('flagStripe', false),
  flagTopGearEnhancements: userFieldReducer('flagTopGearEnhancements', false),
  flagPtr: userFieldReducer('flagPtr', false),
  flagBeta: userFieldReducer('flagBeta', false),
  apiKey: userFieldReducer('apiKey'),
  dataDownloadLastRequest: userFieldReducer('dataDownloadLastRequest'),
  residence: userFieldReducer('residence'),
  blockPremium: userFieldReducer('blockPremium'),
  disputeCount: userFieldReducer('disputeCount'),
});

const userSelector = state => state.user;
const emailSelector = state => state.user.email;
const emailVerifiedSelector = state => Boolean(state.user.emailVerified);
const emailVerifyLastRequestSelector = state => state.user.emailVerifyLastRequest;
const dataDownloadLastRequestSelector = state => state.user.dataDownloadLastRequest;
const isLoggedInSelector = state => !!state.user.username;
const isPatronSelector = state => !!state.user.patreonTitle;
const userRewardsSelector = createSelector(
  userSelector,
  user => Rewards.getRewardsForUser(user),
);
const adsDisabledSelector = createSelector(
  userRewardsSelector,
  userRewards => userRewards.noAds,
);
const queuePrioritySelector = createSelector(
  userRewardsSelector,
  userRewards => userRewards.queuePriority,
);
const iterationsLimitSelector = createSelector(
  userRewardsSelector,
  userRewards => userRewards.iterationsLimit,
);
const timeLimitSelector = createSelector(
  userRewardsSelector,
  userRewards => userRewards.simTimeLimit,
);
const concurrencySelector = createSelector(
  userRewardsSelector,
  userRewards => userRewards.concurrency || 1,
);
const advancedIterationsLimitSelector = createSelector(
  iterationsLimitSelector,
  limit => 2 * limit,
);
const advancedActorLimitSelector = createSelector(
  userRewardsSelector,
  userRewards => userRewards.advancedActorLimit,
);
const adminSelector = createSelector(
  userSelector,
  user => user.admin,
);
const canPtrSelector = createSelector(
  userSelector,
  user => Boolean(user.flagPtr),
);
const isBetaSelector = createSelector(
  userSelector,
  user => Boolean(user.flagBeta),
);

export const selectors = {
  user: userSelector,
  email: emailSelector,
  emailVerified: emailVerifiedSelector,
  emailVerifyLastRequest: emailVerifyLastRequestSelector,
  dataDownloadLastRequest: dataDownloadLastRequestSelector,
  isLoggedIn: isLoggedInSelector,
  userRewards: userRewardsSelector,
  isPatron: isPatronSelector,
  adsDisabled: adsDisabledSelector,
  queuePriority: queuePrioritySelector,
  iterationsLimit: iterationsLimitSelector,
  timeLimit: timeLimitSelector,
  concurrency: concurrencySelector,
  advancedIterationsLimit: advancedIterationsLimitSelector,
  advancedActorLimit: advancedActorLimitSelector,
  admin: adminSelector,
  canPtr: canPtrSelector,
  isBeta: isBetaSelector,
};
